import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { ArrowRight, BadgeCheck, FileText } from 'lucide-react';
import { motion } from 'framer-motion';
import {
    FaShieldAlt,        // For Zero Depreciation Cover
    FaTools,            // For Engine Protection
    FaFileInvoiceDollar,// For Return to Invoice
    FaTruckPickup,      // For Roadside Assistance
    FaOilCan,           // For Consumables Cover
    FaCarSide,          // For Tyre & Alloy Protection
    FaKey,              // For Key Replacement
    FaBatteryFull,      // For Electric Motor & Battery Cover
    FaMapSigns,         // For Off-Road Assistance Add-on
    FaCloudSunRain,
    FaUserLock,
    FaCarCrash,
} from "react-icons/fa";
import FAQSchema from "../../FAQSchema";
import AdBanner from "../AdPage/AdBanner";

function Icon({ id, open }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      className={`${id === open ? "rotate-180" : ""} h-5 w-5 transition-transform`}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
      />
    </svg>
  );
}

const JaguarLandRoverCarInsurance = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [insuranceData, setInsuranceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const faqs = [
    {
      question: "Is JLR insurance expensive in India?",
      answer: "Yes, due to the premium nature and imported parts, insurance tends to be costlier than average brands.",
    },
    {
      question: "Are EVs like the Jaguar I-PACE costlier to insure?",
      answer: "Yes, due to battery components and specialized repairs, I-PACE may attract higher premiums.",
    },
    {
      question: "Is off-road damage covered under standard insurance?",
      answer: "No, off-road incidents require specific add-ons for full coverage.",
    },
    {
      question: "Does Zero Depreciation apply to luxury parts?",
      answer: "Yes, including bumpers, alloy wheels, and expensive body panels.",
    },
    {
      question: "Can I insure a used Range Rover?",
      answer: "Yes, insurance is available for used models post inspection.",
    },
    {
      question: "Do JLR dealerships offer bundled insurance?",
      answer: "Yes, but it’s wise to compare with standalone insurers for better value.",
    },
    {
      question: "Can I switch insurance providers mid-term?",
      answer: "Only during renewal. Mid-term changes are not allowed.",
    },
    {
      question: "What is the best add-on for a Range Rover?",
      answer: "Off-Road Assistance and Engine Protection are highly recommended.",
    },
    {
      question: "Are hybrid or EV parts covered under normal insurance?",
      answer: "Only if you include electric drivetrain or battery-specific add-ons.",
    },
    {
      question: "How is claim processing handled for JLR cars?",
      answer: "Top insurers offer priority claim support for luxury vehicles.",
    },
    {
      question: "Is No Claim Bonus transferable?",
      answer: "Yes, NCB can be transferred when switching insurers.",
    },
    {
      question: "Can I get a multi-year policy?",
      answer: "Yes, most insurers offer long-term coverage up to 3 years.",
    },
    {
      question: "Is tyre damage covered?",
      answer: "Only with a dedicated Tyre Protection add-on.",
    },
    {
      question: "Does insurance cover windshield replacement?",
      answer: "Yes, usually under standard comprehensive policies.",
    },
    {
      question: "Is engine seizure covered?",
      answer: "Only if Engine Protection add-on is selected.",
    },
    {
      question: "Can I cancel my policy mid-way?",
      answer: "Yes, but refund depends on the usage duration and claim status.",
    },
    {
      question: "Do premiums differ by city?",
      answer: "Absolutely — metro cities tend to have higher rates due to risk factors.",
    },
    {
      question: "How can I check my policy status?",
      answer: "Through insurer websites or the VAHAN portal using your vehicle number.",
    },
    {
      question: "What’s the IDV of a new Range Rover?",
      answer: "Typically between ₹2.6 Cr and ₹3.5 Cr depending on variant and location.",
    },
    {
      question: "Does insurance cover accessories?",
      answer: "Yes, but you need to declare them at the time of policy issuance.",
    },
  ];

  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const cardsNew = [
    {
      title: "Zero Depreciation Cover",
      description: "Full reimbursement on all part replacements.",
      icon: <FaShieldAlt className="text-4xl text-red-500 mb-4" />,
    },
    {
      title: "Return to Invoice",
      description: "Restores invoice value in case of total loss or theft.",
      icon: <FaFileInvoiceDollar className="text-4xl text-orange-600 mb-4" />,
    },
    {
      title: "Off-Road Assistance Add-on",
      description: "Coverage for breakdowns or towing in remote areas.",
      icon: <FaMapSigns className="text-4xl text-teal-600 mb-4" />,
    },
    {
      title: "Electric Motor & Battery Cover",
      description: "Covers EV-specific components.",
      icon: <FaBatteryFull className="text-4xl text-green-600 mb-4" />,
    },
    {
      title: "Engine Protection",
      description: "Essential for flood-prone areas or off-road usage.",
      icon: <FaTools className="text-4xl text-yellow-600 mb-4" />,
    },
    {
      title: "Tyre & Alloy Protection",
      description: "Useful for large-diameter wheels and premium alloys.",
      icon: <FaCarSide className="text-4xl text-indigo-600 mb-4" />,
    },
    {
      title: "Roadside Assistance",
      description: "Crucial for highway and off-road travel.",
      icon: <FaTruckPickup className="text-4xl text-blue-500 mb-4" />,
    },
    {
      title: "Consumables Cover",
      description: "Covers engine oil, nuts, and bolts during repairs.",
      icon: <FaOilCan className="text-4xl text-green-600 mb-4" />,
    },
    {
      title: "Key Replacement",
      description: "For high-tech smart key systems.",
      icon: <FaKey className="text-4xl text-purple-600 mb-4" />,
    },
  ];

  const features = [
    { icon: '🛡️', text: 'Reliable & Accurate Estimates' },
    { icon: '⏱️', text: 'Takes Less Than a Minute' },
    { icon: '📉', text: 'Instantly Shows Premium Breakup' },
    { icon: '📄', text: 'No Paperwork or Documentation' },
    { icon: '📲', text: 'Easy to Use on Any Device' },
    { icon: '💰', text: 'Helps You Save on Insurance' },
  ];
const jaguarinsurance = [
    {
      name: "F-Pace",
      path: "/jaguar-fpace-car-insurance", 
      image: "/assets/jaguarfpace.jpeg",
    },
     {
      name: "I-Pace",
      path: "/jaguar-ipace-car-insurance", 
      image: "/assets/jaguaripace.jpeg",
    },
     {
      name: "F-Type",
      path: "/jaguar-ftype-car-insurance", 
      image: "/assets/jaguarftype.jpeg",
    },
    {
      name: "XE",
      path: "/jaguar-xe-car-insurance", 
      image: "/assets/jaguarxe.jpeg",
    },
     {
      name: "XF",
      path: "/jaguar-xf-car-insurance", 
      image: "/assets/jaguarxf.jpeg",
    },
    
    
  ];
  return (
    <>
      <Helmet>
        <title>Jaguar Land Rover Car Insurance in India – Compare Quotes & Buy Online</title>
        <meta
          name="description"
          content="Explore Jaguar Land Rover car insurance options in India. Compare quotes, customize coverage, and buy online to protect your JLR vehicle."
        />
        <meta
          name="keywords"
          content="jaguar land rover car insurance, luxury car insurance india, jaguar f-pace insurance, range rover insurance, land rover defender insurance, car insurance quotes"
        />
            <link
          rel="canonical"
          href="https://thefinmaster.com/jaguar-land-rover-car-insurance"
        />
        <meta
          name="robots"
          content="max-snippet:-1, max-image-preview:standard"
        />
        <meta name="robots" content="max-video-preview:-1" />
        {/* <link rel="canonical" href="https://thefinmaster.com/jaguar-land-rover-car-insurance" />
        <meta name="robots" content="max-snippet:-1, max-image-preview:standard, max-video-preview:-1" />
        <meta property="og:title" content="Jaguar Land Rover Car Insurance in India" />
        <meta property="og:description" content="Compare and buy Jaguar Land Rover car insurance online with ease." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://thefinmaster.com/jaguar-land-rover-car-insurance" />
        <meta name="twitter:card" content="summary_large_image" /> */}
      </Helmet>
      <FAQSchema faqs={faqs} />
      <div className="w-full flex justify-center items-center px-4 mt-20">
        <div className="w-full max-w-5xl h-auto object-contain rounded-lg shadow-md transition duration-300 hover:scale-105">
          <AdBanner displayOrder="1" />
        </div>
      </div>
      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800">Home</Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <span className="text-gray-500">Jaguar Land Rover Car Insurance</span>
          </li>
        </ol>
      </nav>
      <div className="w-full flex flex-col items-center">
        <div className="w-full flex justify-center">
          <div className="w-full sm:w-[95%] flex flex-col sm:flex-row justify-center items-center">
            <div className="w-full sm:w-[50%]">
              <div className="w-[90%]">
                <h1 className="text-3xl md:text-5xl font-bold mt-6 md:mt-0 md:pl-4 pl-6">
                  <span className="text-blue-600">Jaguar Land Rover Car Insurance </span>  
                </h1>
                <p className="text-lg md:text-base lg:text-lg mt-4 pl-4">
                Jaguar is renowned for its iconic design, dynamic performance, and luxury craftsmanship. Whether you drive a sporty F-PACE or a refined XF, keeping your vehicle protected starts with choosing the right insurance. This guide walks you through everything you need to know about insuring a Jaguar, from average premiums and model insights to useful add-ons, renewal tips, and frequently asked questions.
                </p>
              </div>
            </div>
            <div className="w-full sm:w-[50%]">
              <img
                className="w-full max-w-full mt-2"
                src="../assets/rangeinsurance.jpeg"
                alt="Jaguar Land Rover Car"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="flex justify-center items-center p-4 mt-6">
        <div className="bg-white shadow-lg rounded-2xl p-6 max-w-7xl text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">
            About Jaguar Land Rover in India
          </h2>
          <p className="text-gray-600 leading-relaxed">
            Jaguar Land Rover, owned by Tata Motors, has carved out a strong presence in India's luxury automotive space. Jaguar is known for its sleek sports sedans and performance-focused vehicles, while Land Rover specializes in luxury SUVs built for both urban sophistication and rugged adventure. Models like the Jaguar F-Pace, XE, XF, and I-PACE appeal to those who seek dynamic driving, while the Range Rover, Defender, and Discovery series serve luxury SUV enthusiasts.
          </p>
          <p className="text-gray-600 leading-relaxed mt-3">
            JLR vehicles are equipped with advanced 4x4 systems, all-terrain tech, adaptive suspensions, and premium interiors. Many models also feature hybrid or electric drivetrains. These features require insurance plans that account for complex systems and higher repair costs.
          </p>
        </div>
      </div>
       <div className="p-4 sm:p-8 bg-white">
                                      <h2 className="text-xl sm:text-2xl font-bold mb-6 text-center">
                                        Choose Your Car Brand
                                      </h2>
                                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                                        {jaguarinsurance.map((calc, index) => (
                                          <motion.div
                                            key={index}
                                            whileHover={{ scale: 1.04 }}
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.3, delay: index * 0.05 }}
                                          >
                                            <Link
                                              to={calc.path}
                                              className="flex flex-col items-center justify-center text-center border border-gray-200 rounded-lg p-5 bg-white shadow-sm hover:shadow-md transition-all duration-300 h-full min-h-[160px]"
                                            >
                                              <img
                                                src={calc.image}
                                                alt={calc.name}
                                                className="w-16 h-16 object-contain mb-2"
                                              />
                                              <span className="text-md font-medium text-gray-800 break-words leading-snug">
                                                {calc.name}
                                              </span>
                                            </Link>
                                          </motion.div>
                                        ))}
                                      </div>
                                    </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="2" />
        </div>
      </div>
      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 z-0 pointer-events-none">
          <div className="absolute top-[-10%] left-[-10%] w-96 h-96 bg-blue-400 opacity-20 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-400 opacity-20 blur-3xl rounded-full"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-5xl mx-auto bg-white/90 backdrop-blur-lg rounded-2xl shadow-xl p-10"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
            Why JLR Owners Need Tailored Insurance
          </h2>
          <p className="text-gray-700 text-lg leading-relaxed mb-6">
            Insurance for Jaguar and Land Rover vehicles must consider their advanced features, imported components, and higher risk profiles. Whether you're navigating Mumbai traffic in a Jaguar XF or exploring rugged terrains in a Defender, your insurance should match your vehicle’s capabilities and value. Here’s why you need specialized JLR car insurance:
          </p>
          <ul className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 text-gray-700 mb-6">
            <li className="flex items-start gap-3">
              <FaCarCrash className="text-xl text-red-500 mt-1" />
              <span>Financial cover for costly bodywork, sensors, or imported parts</span>
            </li>
            <li className="flex items-start gap-3">
              <FaUserLock className="text-xl text-yellow-500 mt-1" />
              <span>Seamless repairs at authorized JLR service centers</span>
            </li>
            <li className="flex items-start gap-3">
              <FaCloudSunRain className="text-xl text-blue-500 mt-1" />
              <span>Protection against weather-related damage, theft, and total loss</span>
            </li>
            <li className="flex items-start gap-3">
              <FaShieldAlt className="text-xl text-green-500 mt-1" />
              <span>Legal coverage in case of third-party liabilities</span>
            </li>
            <li className="flex items-start gap-3">
              <FaTools className="text-xl text-indigo-500 mt-1" />
              <span>Customization for hybrid, EV, or diesel variants</span>
            </li>
          </ul>
          <div className="bg-indigo-50 border-l-4 border-indigo-400 p-4 rounded-lg text-indigo-700">
            A thoughtfully chosen insurance policy helps protect your investment and enhances your ownership journey.
          </div>
        </motion.div>
      </section>
      <section className="bg-blue-50 py-6 px-4 sm:px-6 md:px-20 lg:px-20 rounded-3xl shadow-lg max-w-6xl mx-auto mt-10">
        <div className="flex flex-col md:flex-row justify-between items-center gap-10">
          <div className="max-w-xl space-y-6 text-center md:text-left">
            <h2 className="text-2xl sm:text-3xl font-bold text-blue-900 leading-tight">
              Calculate Your Car Insurance <br />
              in <span className="text-blue-600">Seconds</span>
            </h2>
            <p className="text-gray-700 text-lg">
              Use our fast and free calculator to estimate your premium — no paperwork, no waiting.
            </p>
            <Link
              to="/financial-tools-calculators/car-insurance-calculator"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 text-lg rounded-full shadow-md transition"
            >
              Start Calculating <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <div className="space-y-4 text-left">
            {features.map((item, index) => (
              <div key={index} className="flex items-center gap-3">
                <span className="text-xl" role="img" aria-label={item.text}>{item.icon}</span>
                <span className="text-gray-800 font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
      <div className="container mx-auto p-6 mt-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Jaguar Land Rover Models and Estimated Insurance Premiums
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Here's a quick look at top-selling JLR models in India, along with approximate Insured Declared Values (IDV) and expected insurance premium ranges:
        </p>
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="block md:hidden">
            {[
              { model: "Jaguar XE", category: "Entry Luxury Sedan", idv: "50–65 Lakh", premium: "₹40,000 – ₹58,000" },
              { model: "Jaguar F-Pace", category: "Performance SUV", idv: "80 Lakh–1.1 Cr", premium: "₹60,000 – ₹85,000" },
              { model: "Jaguar I-PACE (EV)", category: "Electric SUV", idv: "1.2 Cr–1.5 Cr", premium: "₹90,000 – ₹1.3 Lakh" },
              { model: "Range Rover Evoque", category: "Premium SUV", idv: "75–95 Lakh", premium: "₹55,000 – ₹78,000" },
              { model: "Range Rover Sport", category: "Luxury SUV", idv: "1.3 Cr–2 Cr", premium: "₹1 Lakh – ₹1.5 Lakh" },
              { model: "Land Rover Defender", category: "Lifestyle SUV", idv: "95 Lakh–1.6 Cr", premium: "₹80,000 – ₹1.4 Lakh" },
              { model: "Range Rover (Flagship)", category: "Full-size SUV", idv: "2.6 Cr–3.5 Cr", premium: "₹1.6 Lakh – ₹2.4 Lakh" },
            ].map((item, index) => (
              <div key={index} className="border mb-4 p-4 rounded-lg bg-white shadow-md">
                <h3 className="font-semibold text-gray-800 mb-2">{item.model}</h3>
                <div className="text-gray-600 mb-1"><strong>Category:</strong> {item.category}</div>
                <div className="text-gray-600 mb-1"><strong>Approx. IDV:</strong> {item.idv}</div>
                <div className="text-gray-600"><strong>Starting Premium:</strong> {item.premium}</div>
              </div>
            ))}
          </div>
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full border-collapse border border-gray-300">
              <thead className="bg-gray-100 text-gray-800">
                <tr>
                  <th className="p-3 border border-gray-300 text-left">Model</th>
                  <th className="p-3 border border-gray-300 text-left">Category</th>
                  <th className="p-3 border border-gray-300 text-left">Approx. IDV (₹)</th>
                  <th className="p-3 border border-gray-300 text-left">Starting Premium (₹)</th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                {[
                  ["Jaguar XE", "Entry Luxury Sedan", "50–65 Lakh", "₹40,000 – ₹58,000"],
                  ["Jaguar F-Pace", "Performance SUV", "80 Lakh–1.1 Cr", "₹60,000 – ₹85,000"],
                  ["Jaguar I-PACE (EV)", "Electric SUV", "1.2 Cr–1.5 Cr", "₹90,000 – ₹1.3 Lakh"],
                  ["Range Rover Evoque", "Premium SUV", "75–95 Lakh", "₹55,000 – ₹78,000"],
                  ["Range Rover Sport", "Luxury SUV", "1.3 Cr–2 Cr", "₹1 Lakh – ₹1.5 Lakh"],
                  ["Land Rover Defender", "Lifestyle SUV", "95 Lakh–1.6 Cr", "₹80,000 – ₹1.4 Lakh"],
                  ["Range Rover (Flagship)", "Full-size SUV", "2.6 Cr–3.5 Cr", "₹1.6 Lakh – ₹2.4 Lakh"],
                ].map(([model, category, idv, premium], index) => (
                  <tr key={index} className="border border-gray-300">
                    <td className="p-3 border border-gray-300">{model}</td>
                    <td className="p-3 border border-gray-300">{category}</td>
                    <td className="p-3 border border-gray-300">{idv}</td>
                    <td className="p-3 border border-gray-300">{premium}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="container mx-auto p-6 mt-6">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Recommended Add-Ons for JLR Car Insurance
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Given the premium nature and off-road capability of JLR vehicles, consider these key add-ons for better protection:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mt-4">
          {cardsNew.map((card, index) => (
            <div
              key={index}
              className="border rounded-lg shadow-lg p-6 flex flex-col items-center text-center"
            >
              {card.icon}
              <h3 className="text-lg font-semibold mb-2">{card.title}</h3>
              <p className="text-gray-600">{card.description}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="3" />
        </div>
      </div>
      <div className="mx-auto container p-6 mt-6">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Top Insurers for JLR Car Insurance in India
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Luxury vehicle owners prefer insurers with premium service standards and strong claim support:
        </p>
        <div className="mt-6 grid md:grid-cols-3 gap-6 text-left">
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">⚡</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">Tata AIG</h3>
            <p className="text-gray-700 text-sm mt-1">Offers smooth claims and quick service for JLR cars.</p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🚗</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">HDFC ERGO</h3>
            <p className="text-gray-700 text-sm mt-1">Known for luxury car compatibility and extensive garage network.</p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🏥</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">ICICI Lombard</h3>
            <p className="text-gray-700 text-sm mt-1">Offers hybrid/EV-friendly coverage and quick claim turnarounds.</p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">💰</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">Reliance General</h3>
            <p className="text-gray-700 text-sm mt-1">Flexible coverage options, including EV and off-road add-ons.</p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">📱</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">ACKO</h3>
            <p className="text-gray-700 text-sm mt-1">Offers personalized quotes and paperless experience for luxury cars.</p>
          </div>
        </div>
      </div>
      <div className="w-full px-2 py-6 bg-gradient-to-br from-sky-50 via-white to-sky-100 dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-6xl mx-auto grid gap-10">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-sky-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-8 h-8 text-sky-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                Tips to Save on JLR Insurance Premium
              </span>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              Luxury insurance doesn't have to be overpriced. Use these tips to save smartly:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Compare quotes from 4–5 insurers before buying or renewing.</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Retain your No Claim Bonus (NCB).</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Install GPS trackers and anti-theft devices for added discounts.</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Bundle policies if you own more than one vehicle.</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Opt for voluntary deductibles to reduce yearly premiums.</span>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-emerald-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-6">
              <BadgeCheck className="w-8 h-8 text-emerald-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                Documents Required for Jaguar Land Rover Insurance
              </span>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              When buying or renewing your JLR policy, keep these handy:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Registration Certificate (RC) copy</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Existing insurance policy (for renewals)</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Valid PUC certificate</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Identity proof – Aadhaar or PAN card</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl sm:col-span-2">
                <span>Recent passport-size photograph</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto py-6 px-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-center mb-8">
          Jaguar Land Rover Car Insurance FAQs
        </h2>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border rounded-lg overflow-hidden shadow-md"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
                aria-expanded={activeIndex === index}
                aria-controls={`faq-answer-${index}`}
              >
                {faq.question}
                <Icon id={index} open={activeIndex} />
              </button>
              {activeIndex === index && (
                <div id={`faq-answer-${index}`} className="p-4 bg-gray-100">
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none z-0">
          <div className="absolute top-[-20%] left-[-10%] w-96 h-96 bg-indigo-300 opacity-30 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-300 opacity-30 blur-3xl rounded-full"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-4xl mx-auto bg-white/90 backdrop-blur-sm border border-gray-200 shadow-xl rounded-2xl p-10"
        >
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="text-indigo-600 text-5xl">
              <FaCarSide />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Conclusion
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed mb-4">
                Jaguar Land Rover vehicles are more than just machines — they’re statements of performance, prestige, and personality. Whether you drive a sleek Jaguar or a go-anywhere Land Rover, insuring your vehicle with the right plan ensures peace of mind and preserves the luxury ownership experience.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed">
                At TheFinMaster, we help you find policies tailored to your JLR model, driving needs, and lifestyle. Compare quotes, explore add-ons, and make smarter insurance choices — all in one place.
              </p>
            </div>
          </div>
        </motion.div>
      </section>
    </>
  );
};

export default JaguarLandRoverCarInsurance;